/**
 * Tatbikat yönetimi metinleri (ADR 0128 Faz 1: Master Library). Ortağın belgesindeki adlar
 * İngilizcede aynen (Drill Library, Drill Code, Scenario Description…). Hata ve doğrulama
 * anahtarları sunucunun kodlarıyla eşleşir.
 */

export const drillsEn = {
  close: 'Close',
  library: {
    title: 'Drill Library',
    description:
      'Company master library of drill templates: scenario, execution steps, roles, equipment and performance criteria. Content changes create a new template version; earlier versions stay unchanged.',
    shipDescription:
      'The company drill library, received from the office. Read-only on board; the office edits templates.',
    error: 'The drill library could not be loaded.',
    categories: 'Categories',
    templates: 'Drill Templates',
    templateList: 'Drill template list',
    categoryList: 'Category list',
  },
  tabs: {
    library: 'Library',
    categories: 'Categories',
    importExport: 'Import / Export',
  },
  stats: {
    templates: 'Templates',
    categories: 'Categories',
    active: 'Active',
    inactive: 'Inactive',
  },
  filters: {
    allCategories: 'All categories',
    search: 'Search code, name or reference',
    status: 'Status',
    statuses: { all: 'All statuses', active: 'Active', inactive: 'Inactive' },
  },
  columns: {
    code: 'Drill Code',
    name: 'Drill Name',
    category: 'Category',
    categoryName: 'Category',
    frequency: 'Frequency',
    version: 'Version',
    status: 'Status',
    actions: 'Actions',
    templates: 'Templates',
    date: 'Date',
    by: 'By',
  },
  status: { active: 'Active', inactive: 'Inactive' },
  showing: 'Showing {{from}}–{{to}} of {{total}}',
  empty: {
    library: 'The drill library is empty. Load the default library or import an Excel file.',
    filtered: 'No drill templates match the filters.',
    categories: 'No categories yet.',
  },
  frequency: {
    monthly: 'Monthly',
    bimonthly: 'Every 2 months',
    quarterly: 'Quarterly',
    semiannual: 'Every 6 months',
    annual: 'Annual',
    custom: 'Every {{count}} months',
    customOption: 'Custom (months)',
  },
  criterionKinds: { time: 'time', rating: 'rating' },
  sources: { default: 'Felix default library', import: 'Excel import', manual: 'Entered manually' },
  fields: {
    code: 'Drill Code',
    name: 'Drill Name',
    category: 'Category',
    subcategory: 'Subcategory',
    categoryName: 'Category Name',
    parent: 'Main Category',
    sortOrder: 'Sort Order',
    active: 'Active',
    frequency: 'Frequency',
    months: 'Interval (months)',
    ruleCode: 'Requirement',
    regulatoryReference: 'Flag / Regulatory Reference',
    smsProcedure: 'SMS Procedure',
    vesselTypes: 'Applicable Vessel Types',
    objective: 'Objective',
    scenario: 'Scenario Description',
    equipment: 'Required Equipment',
    roles: 'Roles / Participants',
    preparation: 'Preparation Checklist',
    steps: 'Execution Steps',
    safety: 'Safety Precautions',
    criteria: 'Performance Criteria',
    debriefing: 'Debriefing Questions',
    correctiveActions: 'Recommended Corrective Actions',
    reportTemplate: 'Report Template',
    source: 'Source',
    content: 'Content',
  },
  detail: {
    title: 'Drill Template',
    loading: 'Loading drill template',
    allVessels: 'All vessels',
    versions: 'Versions',
    versionNote:
      'Each version is kept unchanged. A drill record keeps the version it was performed with, so later library changes do not alter past reports.',
  },
  edit: {
    titleNew: 'New Drill Template',
    titleEdit: 'Edit {{code}}',
    versionHint:
      'Saving a change creates a new version of the template. Saving without changes does not.',
    codeFixed: 'The code cannot be changed.',
    vesselTypesHint: 'One vessel type per line; leave empty for all vessels.',
    hints: {
      steps: 'One step per line, in order.',
      equipment: 'One item per line.',
      roles: 'One per line, as "Role: duty".',
      preparation: 'One item per line.',
      safety: 'One precaution per line.',
      criteria: 'One per line, as "Label (time)" or "Label (rating)".',
      debriefing: 'One question per line.',
      correctiveActions: 'One action per line.',
    },
  },
  category: {
    titleNew: 'New Category',
    titleEdit: 'Edit category {{code}}',
    parentHint: 'Leave empty for a main category; choose one to add a subcategory.',
    hasChildren: 'This category has subcategories; it stays a main category.',
  },
  actions: {
    newTemplate: 'New Drill Template',
    addCategory: 'Add Category',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    preview: 'Preview',
    import: 'Import',
    export: 'Export to Excel',
    loadDefaults: 'Load default library',
  },
  importer: {
    title: 'Import from Excel',
    description:
      'One row per drill template on the "Drill Library" sheet. New codes are added; an existing code with different content becomes a new version; templates not in the file are not changed.',
    file: 'Excel file (.xlsx)',
    filePlaceholder: 'Choose a file',
    summary: 'Import summary',
    previewNote: 'Preview: nothing has been written yet.',
    importedNote: 'Imported.',
    counts:
      '{{added}} new, {{newVersion}} new versions, {{unchanged}} unchanged; {{categories}} new categories.',
    skippedRows: '{{count}} rows with errors are skipped.',
    confirmWithErrors: 'Import the valid rows and skip {{count}} rows with errors?',
    row: 'Row',
    field: 'Field',
    problem: 'Problem',
    errors: 'Row errors',
    statuses: { added: 'New', new_version: 'New version', unchanged: 'Unchanged' },
    defaultsTitle: 'Felix Default Library',
    defaultsText:
      'DRILL-LIB-1.0: 12 categories and 26 drill templates (fire, abandon ship, man overboard, pollution, security, navigation, machinery, medical, enclosed space, damage control, cargo, ship-specific). Only missing entries are added; your changes are kept.',
    defaultsDone:
      '{{version}}: {{categories}} categories, {{subcategories}} subcategories and {{templates}} templates added.',
    defaultsNone: '{{version}}: nothing missing.',
    exportTitle: 'Export',
    exportText: 'The library in the import format. The file can be edited and imported again.',
  },
  issues: {
    REQUIRED: 'required',
    TOO_LONG: 'too long',
    TOO_MANY: 'too many entries',
    INVALID_CODE: 'invalid code (A–Z, 0–9, "-", ".")',
    INVALID_OPTION: 'not a valid option',
    INVALID_KEY: 'invalid key',
    DUPLICATE_KEY: 'repeated entry',
    DUPLICATE_ROW: 'code repeated in the file',
    OUT_OF_RANGE: 'out of range',
    INVALID_TEXT: 'invalid text',
    INVALID_NUMBER: 'not a whole number',
    INVALID_ID: 'not selected',
    UNKNOWN_CATEGORY: 'unknown category',
    INACTIVE_CATEGORY: 'category is inactive',
  },
  errors: {
    GENERIC: 'The operation failed ({{code}}).',
    FORBIDDEN: 'You do not have permission for this.',
    INVALID_FIELDS: 'Check the fields: {{fields}}.',
    DUPLICATE_DRILL_CODE: 'The code {{detail}} is already used.',
    DRILL_CODE_FIXED: 'The code cannot be changed.',
    SUBCATEGORY_MISMATCH: 'The subcategory does not belong to the chosen category.',
    INACTIVE_CATEGORY: 'The category {{detail}} is inactive.',
    CATEGORY_TOO_DEEP: 'Categories have two levels: a subcategory cannot have subcategories.',
    INVALID_CATEGORY: 'Choose a main category.',
    ROW_VERSION_CONFLICT: 'Someone else changed this entry. Close and open it again.',
    SHEET_MISSING: 'The file has no "Drill Library" sheet.',
    INVALID_HEADER: 'The "Drill Library" sheet has missing columns.',
    INVALID_FILE: 'The file could not be read as Excel.',
    OFFICE_IS_WRITER: 'The drill library is maintained by the office.',
    NETWORK: 'No connection to the server.',
  },
};

export const drillsTr: typeof drillsEn = {
  close: 'Kapat',
  library: {
    title: 'Tatbikat Kütüphanesi',
    description:
      'Şirketin tatbikat şablonları: senaryo, uygulama adımları, roller, ekipman ve performans ölçütleri. İçerik değişikliği şablonun yeni sürümünü açar; önceki sürümler aynen kalır.',
    shipDescription:
      'Şirketin tatbikat kütüphanesi, ofisten iner. Gemide salt okunur; şablonları ofis düzenler.',
    error: 'Tatbikat kütüphanesi yüklenemedi.',
    categories: 'Kategoriler',
    templates: 'Tatbikat Şablonları',
    templateList: 'Tatbikat şablonu listesi',
    categoryList: 'Kategori listesi',
  },
  tabs: {
    library: 'Kütüphane',
    categories: 'Kategoriler',
    importExport: 'İçe / Dışa Aktar',
  },
  stats: {
    templates: 'Şablon',
    categories: 'Kategori',
    active: 'Aktif',
    inactive: 'Pasif',
  },
  filters: {
    allCategories: 'Bütün kategoriler',
    search: 'Kod, ad ya da dayanak ara',
    status: 'Durum',
    statuses: { all: 'Bütün durumlar', active: 'Aktif', inactive: 'Pasif' },
  },
  columns: {
    code: 'Tatbikat Kodu',
    name: 'Tatbikat Adı',
    category: 'Kategori',
    categoryName: 'Kategori',
    frequency: 'Sıklık',
    version: 'Sürüm',
    status: 'Durum',
    actions: 'İşlemler',
    templates: 'Şablon',
    date: 'Tarih',
    by: 'Yapan',
  },
  status: { active: 'Aktif', inactive: 'Pasif' },
  showing: '{{total}} kayıttan {{from}}–{{to}}',
  empty: {
    library: 'Tatbikat kütüphanesi boş. Varsayılan kütüphaneyi yükleyin ya da Excel içe aktarın.',
    filtered: 'Süzgeçlere uyan tatbikat şablonu yok.',
    categories: 'Henüz kategori yok.',
  },
  frequency: {
    monthly: 'Aylık',
    bimonthly: '2 ayda bir',
    quarterly: '3 ayda bir',
    semiannual: '6 ayda bir',
    annual: 'Yıllık',
    custom: '{{count}} ayda bir',
    customOption: 'Özel (ay)',
  },
  criterionKinds: { time: 'süre', rating: 'puan' },
  sources: {
    default: 'Felix varsayılan kütüphanesi',
    import: 'Excel içe aktarma',
    manual: 'Elle girildi',
  },
  fields: {
    code: 'Tatbikat Kodu',
    name: 'Tatbikat Adı',
    category: 'Kategori',
    subcategory: 'Alt Kategori',
    categoryName: 'Kategori Adı',
    parent: 'Ana Kategori',
    sortOrder: 'Sıra',
    active: 'Aktif',
    frequency: 'Sıklık',
    months: 'Aralık (ay)',
    ruleCode: 'Gereklilik',
    regulatoryReference: 'Bayrak / Mevzuat Dayanağı',
    smsProcedure: 'SMS Prosedürü',
    vesselTypes: 'Uygulanan Gemi Tipleri',
    objective: 'Amaç',
    scenario: 'Senaryo',
    equipment: 'Gerekli Ekipman',
    roles: 'Roller / Katılımcılar',
    preparation: 'Hazırlık Listesi',
    steps: 'Uygulama Adımları',
    safety: 'Güvenlik Önlemleri',
    criteria: 'Performans Ölçütleri',
    debriefing: 'Değerlendirme Soruları',
    correctiveActions: 'Önerilen Düzeltici Faaliyetler',
    reportTemplate: 'Rapor Şablonu',
    source: 'Kaynak',
    content: 'İçerik',
  },
  detail: {
    title: 'Tatbikat Şablonu',
    loading: 'Tatbikat şablonu yükleniyor',
    allVessels: 'Bütün gemiler',
    versions: 'Sürümler',
    versionNote:
      'Her sürüm aynen saklanır. Tatbikat kaydı yapıldığı sürümü tutar; kütüphanenin sonraki değişiklikleri geçmiş raporları değiştirmez.',
  },
  edit: {
    titleNew: 'Yeni Tatbikat Şablonu',
    titleEdit: '{{code}} düzenle',
    versionHint:
      'Değişikliği kaydetmek şablonun yeni sürümünü açar. Değişiklik yoksa sürüm açılmaz.',
    codeFixed: 'Kod değiştirilemez.',
    vesselTypesHint: 'Her satıra bir gemi tipi; bütün gemiler için boş bırakın.',
    hints: {
      steps: 'Her satıra bir adım, sırasıyla.',
      equipment: 'Her satıra bir kalem.',
      roles: 'Her satıra bir tane, "Rol: görev" biçiminde.',
      preparation: 'Her satıra bir madde.',
      safety: 'Her satıra bir önlem.',
      criteria: 'Her satıra bir tane, "Ad (time)" ya da "Ad (rating)" biçiminde.',
      debriefing: 'Her satıra bir soru.',
      correctiveActions: 'Her satıra bir faaliyet.',
    },
  },
  category: {
    titleNew: 'Yeni Kategori',
    titleEdit: '{{code}} kategorisini düzenle',
    parentHint: 'Ana kategori için boş bırakın; alt kategori için ana kategoriyi seçin.',
    hasChildren: 'Bu kategorinin alt kategorileri var; ana kategori kalır.',
  },
  actions: {
    newTemplate: 'Yeni Tatbikat Şablonu',
    addCategory: 'Kategori Ekle',
    edit: 'Düzenle',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    preview: 'Önizle',
    import: 'İçe Aktar',
    export: "Excel'e Aktar",
    loadDefaults: 'Varsayılan kütüphaneyi yükle',
  },
  importer: {
    title: "Excel'den İçe Aktar",
    description:
      '"Drill Library" sayfasında her satır bir tatbikat şablonu. Yeni kodlar eklenir; içeriği farklı olan mevcut kod yeni sürüm olur; dosyada olmayan şablona dokunulmaz.',
    file: 'Excel dosyası (.xlsx)',
    filePlaceholder: 'Dosya seçin',
    summary: 'İçe aktarma özeti',
    previewNote: 'Önizleme: henüz hiçbir şey yazılmadı.',
    importedNote: 'İçe aktarıldı.',
    counts:
      '{{added}} yeni, {{newVersion}} yeni sürüm, {{unchanged}} değişmedi; {{categories}} yeni kategori.',
    skippedRows: 'Hatalı {{count}} satır atlanır.',
    confirmWithErrors: 'Geçerli satırlar içe aktarılsın, hatalı {{count}} satır atlansın mı?',
    row: 'Satır',
    field: 'Alan',
    problem: 'Sorun',
    errors: 'Satır hataları',
    statuses: { added: 'Yeni', new_version: 'Yeni sürüm', unchanged: 'Değişmedi' },
    defaultsTitle: 'Felix Varsayılan Kütüphanesi',
    defaultsText:
      'DRILL-LIB-1.0: 12 kategori ve 26 tatbikat şablonu (yangın, gemiyi terk, denize adam düştü, kirlilik, güvenlik, seyir, makine, tıbbi, kapalı alan, hasar kontrol, yük, gemiye özgü). Yalnız eksikler eklenir; değişiklikleriniz korunur.',
    defaultsDone:
      '{{version}}: {{categories}} kategori, {{subcategories}} alt kategori ve {{templates}} şablon eklendi.',
    defaultsNone: '{{version}}: eksik yok.',
    exportTitle: 'Dışa Aktar',
    exportText: 'Kütüphane içe aktarma biçiminde. Dosya düzenlenip yeniden içe aktarılabilir.',
  },
  issues: {
    REQUIRED: 'zorunlu',
    TOO_LONG: 'çok uzun',
    TOO_MANY: 'çok fazla giriş',
    INVALID_CODE: 'geçersiz kod (A–Z, 0–9, "-", ".")',
    INVALID_OPTION: 'geçerli bir seçenek değil',
    INVALID_KEY: 'geçersiz anahtar',
    DUPLICATE_KEY: 'tekrarlanan giriş',
    DUPLICATE_ROW: 'kod dosyada tekrar ediyor',
    OUT_OF_RANGE: 'aralık dışında',
    INVALID_TEXT: 'geçersiz metin',
    INVALID_NUMBER: 'tam sayı değil',
    INVALID_ID: 'seçilmedi',
    UNKNOWN_CATEGORY: 'bilinmeyen kategori',
    INACTIVE_CATEGORY: 'kategori pasif',
  },
  errors: {
    GENERIC: 'İşlem yapılamadı ({{code}}).',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    INVALID_FIELDS: 'Alanları kontrol edin: {{fields}}.',
    DUPLICATE_DRILL_CODE: '{{detail}} kodu zaten kullanılıyor.',
    DRILL_CODE_FIXED: 'Kod değiştirilemez.',
    SUBCATEGORY_MISMATCH: 'Alt kategori seçilen kategoriye ait değil.',
    INACTIVE_CATEGORY: '{{detail}} kategorisi pasif.',
    CATEGORY_TOO_DEEP: 'Kategoriler iki düzeylidir: alt kategorinin alt kategorisi olmaz.',
    INVALID_CATEGORY: 'Bir ana kategori seçin.',
    ROW_VERSION_CONFLICT: 'Bu kaydı başka biri değiştirdi. Kapatıp yeniden açın.',
    SHEET_MISSING: 'Dosyada "Drill Library" sayfası yok.',
    INVALID_HEADER: '"Drill Library" sayfasında eksik sütunlar var.',
    INVALID_FILE: 'Dosya Excel olarak okunamadı.',
    OFFICE_IS_WRITER: 'Tatbikat kütüphanesini ofis yönetir.',
    NETWORK: 'Sunucuya bağlantı yok.',
  },
};
